import { trackCotacaoApresentacaoUpdate } from "@/analytics/tracking";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { updateCotacaoField } from "@/services/flowQuote";
import { CotacaoEcommerce } from "@/types/Cotacao";
import { formatDateToYMD } from "@/utils/format";
import { addDays, format, isBefore, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { GoAlert } from "react-icons/go";

interface DeliveryDateCardProps {
  cotacao: CotacaoEcommerce;
  onDateUpdate: (cve_codigo: string) => Promise<void>;
}

export function DeliveryDateCard({
  cotacao,
  onDateUpdate,
}: DeliveryDateCardProps) {
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canEditDate =
    cotacao?.cls_codigo !== null && Number(cotacao.cls_codigo) < 3;

  useEffect(() => {
    if (cotacao.cve_data_previsao_recebimento) {
      try {
        const parsedDate = parse(
          cotacao.cve_data_previsao_recebimento,
          "dd/MM/yyyy",
          new Date(),
        );
        setDate(parsedDate);
      } catch (err) {
        console.error("Failed to parse date:", err);
        setDate(undefined);
      }
    }
  }, [cotacao.cve_data_previsao_recebimento]);

  if (canEditDate) {
    return null;
  }

  const minDate = addDays(new Date(), 1);
  const isDateDisabled = (date: Date) => {
    if (isBefore(date, minDate)) return true;

    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  const handleDateChange = async (newDate: Date | undefined) => {
    if (!newDate || !cotacao.cve_codigo) return;

    setIsUpdating(true);
    setError(null);

    try {
      await updateCotacaoField(
        cotacao.cve_codigo,
        "cve_data_previsao_recebimento",
        formatDateToYMD(newDate),
      );

      await onDateUpdate(cotacao.cve_codigo);

      trackCotacaoApresentacaoUpdate(cotacao, "date_changed", {
        new_date: formatDateToYMD(newDate),
        old_date: cotacao.cve_data_previsao_recebimento,
      });

      setDate(newDate);
    } catch (err) {
      console.error("Failed to update date:", err);
      setError("Não foi possível atualizar a data. Tente novamente.");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold text-n-green">
        Data de transporte:
      </h3>

      <div className="flex flex-col md:flex-row items-start gap-3 py-3 px-6 border border-n-atention rounded-xl">
        <div className="flex items-start gap-4 flex-1">
          <div className="flex-shrink-0 mt-0.5">
            <GoAlert className="text-n-atention h-6 w-6" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-n-text leading-tight">
              {canEditDate
                ? "Confirme a data prevista para o recebimento do veículo na Nacional."
                : "A data prevista para o transporte do seu veículo é:"}
            </p>
            {canEditDate ? (
              <p className="text-xs text-gray-600 mt-0.5">
                Se preciso altere a data do seu recebimento.
              </p>
            ) : (
              <p className="text-xs text-gray-600 mt-0.5">
                Atente-se também às outras informações do seu veículo e
                contrato.
              </p>
            )}
          </div>
        </div>

        {canEditDate ? (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="input"
                size="md"
                disabled={isUpdating}
                className="h-[32px] w-full md:w-fit flex-shrink-0 md:justify-start rounded-full justify-center md:text-left text-center text-sm font-normal border-zinc-300 border bg-white hover:bg-gray-50"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? (
                  format(date, "dd/MM/yyyy", { locale: ptBR })
                ) : (
                  <span>Selecione</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={date}
                onSelect={handleDateChange}
                disabled={isDateDisabled}
                initialFocus
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>
        ) : (
          <div className="h-[32px] w-full md:w-fit flex-shrink-0 flex items-center justify-center md:justify-start px-4 rounded-full text-sm font-normal border-zinc-300 border bg-gray-50">
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? (
              format(date, "dd/MM/yyyy", { locale: ptBR })
            ) : (
              <span>Não definida</span>
            )}
          </div>
        )}
      </div>

      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </div>
  );
}
